import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MapPin, Navigation, RefreshCw, Truck, Clock } from 'lucide-react';
import { Button } from '../ui/button';

const REFRESH_INTERVAL = 15000; // 15 seconds

const LiveDriverMap = ({ websiteId, token, API_URL, drivers = [], routes = [], theme = 'dark' }) => {
  const dash = theme === 'dash';
  const card = dash
    ? 'bg-white border border-slate-200 rounded-xl p-6 space-y-4 shadow-sm'
    : 'bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 space-y-4';
  const title = dash ? 'text-lg font-bold text-slate-900' : 'text-lg font-bold';
  const muted = dash ? 'text-xs text-slate-500' : 'text-xs text-zinc-400';
  const outlineBtn = dash ? 'border-slate-300 text-xs' : 'border-zinc-700 text-xs';
  const driverCard = dash
    ? 'p-3 rounded-lg border cursor-pointer transition bg-slate-50 border-slate-200 hover:border-indigo-300'
    : 'p-3 rounded-lg border cursor-pointer transition bg-zinc-800/40 border-zinc-700 hover:border-cyan-500/50';
  const driverCardActive = dash
    ? 'p-3 rounded-lg border cursor-pointer transition bg-indigo-50 border-indigo-300'
    : 'p-3 rounded-lg border cursor-pointer transition bg-cyan-500/10 border-cyan-500/50';
  const [liveDrivers, setLiveDrivers] = useState(drivers);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const refreshDrivers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/commerce/drivers`, { headers });
      if (res.ok) {
        const data = await res.json();
        setLiveDrivers(data);
        setLastRefresh(new Date());
      }
    } catch { /* silent */ }
  }, [websiteId, API_URL, token]);

  useEffect(() => {
    if (autoRefresh) {
      refreshDrivers();
      intervalRef.current = setInterval(refreshDrivers, REFRESH_INTERVAL);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, refreshDrivers]);

  useEffect(() => {
    setLiveDrivers(drivers);
  }, [drivers]);

  const activeDrivers = liveDrivers.filter(d => d.is_active && d.last_lat != null && d.last_lng != null);
  const inactiveDrivers = liveDrivers.filter(d => !d.is_active || d.last_lat == null);

  const activeRoute = routes.find(r => r.status === 'active');

  // Calculate map bounds
  const allPoints = [
    ...activeDrivers.map(d => ({ lat: d.last_lat, lng: d.last_lng })),
    ...(activeRoute?.stops || []).map(s => ({ lat: s.lat, lng: s.lng })),
  ];

  const center = allPoints.length > 0
    ? { lat: allPoints.reduce((s, p) => s + p.lat, 0) / allPoints.length, lng: allPoints.reduce((s, p) => s + p.lng, 0) / allPoints.length }
    : { lat: 40.7128, lng: -74.006 };

  const timeSince = (isoStr) => {
    if (!isoStr) return 'Never';
    const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  return (
    <div className={card}>
      <div className="flex items-center justify-between">
        <h3 className={`${title} flex items-center gap-2`}>
          <Navigation className={`w-5 h-5 ${dash ? 'text-indigo-600' : 'text-cyan-400'}`} />
          Live Driver Tracking
        </h3>
        <div className="flex items-center gap-3">
          <label className={`flex items-center gap-2 ${muted}`}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <Button onClick={refreshDrivers} size="sm" variant="outline" className={outlineBtn}>
            <RefreshCw className="w-3 h-3 mr-1" />Refresh
          </Button>
        </div>
      </div>

      {lastRefresh && (
        <p className="text-xs text-zinc-500 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Last updated: {lastRefresh.toLocaleTimeString()}
        </p>
      )}

      {/* Map visualization using CSS grid positioning */}
      <div className="relative bg-zinc-800 rounded-xl overflow-hidden" style={{ height: '320px' }}>
        {/* Grid background */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />

        {activeDrivers.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-zinc-500 text-sm px-6">
            <div className="text-center max-w-sm">
              <Truck className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No active drivers with GPS data</p>
              <p className="text-xs mt-2 text-zinc-400">
                Add a driver above, copy their driver app link, open it on a phone, tap Start GPS, then return here. Map refreshes every 15s.
              </p>
            </div>
          </div>
        )}

        {/* Plot drivers on the map area */}
        {activeDrivers.map((driver, idx) => {
          const total = activeDrivers.length;
          // Distribute drivers evenly across the map area
          const angle = (idx / Math.max(total, 1)) * 2 * Math.PI;
          const radius = total > 1 ? 30 : 0;
          const x = 50 + radius * Math.cos(angle);
          const y = 50 + radius * Math.sin(angle);

          return (
            <button
              key={driver.id}
              type="button"
              onClick={() => setSelectedDriver(selectedDriver === driver.id ? null : driver.id)}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all ${
                selectedDriver === driver.id
                  ? 'bg-cyan-500 ring-4 ring-cyan-500/30 scale-125'
                  : 'bg-emerald-500 hover:bg-emerald-400 hover:scale-110'
              }`}>
                <Truck className="w-5 h-5 text-white" />
              </div>
              {/* Pulse animation for recently active drivers */}
              {driver.last_location_at && (Date.now() - new Date(driver.last_location_at).getTime()) < 300000 && (
                <span className="absolute inset-0 rounded-full bg-emerald-500/30 animate-ping" />
              )}
              {/* Label */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-medium text-white bg-zinc-900/80 px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                {driver.name}
              </div>
            </button>
          );
        })}

        {/* Active route stops */}
        {activeRoute && activeRoute.stops?.map((stop, idx) => {
          const total = activeRoute.stops.length;
          const x = 20 + (idx / Math.max(total - 1, 1)) * 60;
          const y = 75;
          return (
            <div
              key={stop.order_id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                stop.status === 'delivered' ? 'bg-emerald-600 text-white' :
                stop.status === 'en_route' ? 'bg-amber-500 text-black' :
                'bg-zinc-600 text-white'
              }`}>
                {idx + 1}
              </div>
            </div>
          );
        })}

        {/* Map center marker */}
        <div className="absolute bottom-3 right-3 text-xs text-zinc-500 bg-zinc-900/80 px-2 py-1 rounded">
          {center.lat.toFixed(4)}, {center.lng.toFixed(4)}
        </div>
      </div>

      {/* Driver status cards */}
      <div className="grid md:grid-cols-2 gap-3">
        {activeDrivers.map(driver => (
          <div
            key={driver.id}
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              selectedDriver === driver.id
                ? 'border-cyan-500 bg-cyan-500/10'
                : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
            }`}
            onClick={() => setSelectedDriver(selectedDriver === driver.id ? null : driver.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="font-medium text-sm">{driver.name}</span>
              </div>
              <span className="text-xs text-zinc-400">{timeSince(driver.last_location_at)}</span>
            </div>
            <div className="mt-1 text-xs text-zinc-500 flex items-center gap-2">
              <MapPin className="w-3 h-3" />
              {driver.last_lat?.toFixed(5)}, {driver.last_lng?.toFixed(5)}
              {driver.vehicle && <span className="ml-auto">{driver.vehicle}</span>}
            </div>
          </div>
        ))}
        {inactiveDrivers.map(driver => (
          <div key={driver.id} className="p-3 rounded-lg border border-zinc-800 bg-zinc-900/30 opacity-60">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-zinc-600" />
              <span className="text-sm">{driver.name}</span>
              <span className="text-xs text-zinc-600 ml-auto">{driver.is_active ? 'No GPS' : 'Inactive'}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Active route summary */}
      {activeRoute && (
        <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold flex items-center gap-2">
              <Navigation className="w-4 h-4 text-amber-400" />
              Active Route
            </span>
            <span className="text-xs text-zinc-400">
              {activeRoute.total_distance_km} km, ~{activeRoute.estimated_minutes} min
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {activeRoute.stops?.map((stop, idx) => (
              <span
                key={stop.order_id}
                className={`text-xs px-2 py-1 rounded ${
                  stop.status === 'delivered' ? 'bg-emerald-500/20 text-emerald-300' :
                  stop.status === 'en_route' ? 'bg-amber-500/20 text-amber-300' :
                  'bg-zinc-700 text-zinc-300'
                }`}
              >
                #{idx + 1} {stop.order_number || stop.order_id.slice(0, 6)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveDriverMap;
